import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { paymentsAPI, settingsAPI } from '../services/api';

type PlanRow = {
  feature: string;
  free: string;
  silver: string;
};

const PricingTable: React.FC<{
  title: string;
  subtitle: string;
  freeLabel: string;
  silverLabel: string;
  rows: PlanRow[];
  ctaLabel: string;
  paymentLive?: boolean;
  processingPayment?: boolean;
  alreadySubscribed?: boolean;
  onChoose: () => void;
  onTalkToSales: () => void;
}> = ({ title, subtitle, freeLabel, silverLabel, rows, ctaLabel, paymentLive, processingPayment, alreadySubscribed, onChoose, onTalkToSales }) => (
  <section className="card pricing-card">
    <div className="pricing-card__head">
      <div>
        <h2 className="h2">{title}</h2>
        <p className="muted">{subtitle}</p>
      </div>
      <span className="badge blue">Silver Recommended</span>
    </div>

    <div className="pricing-table-wrap">
      <table className="table pricing-table">
        <thead>
          <tr>
            <th>Feature</th>
            <th>{freeLabel}</th>
            <th className="pricing-table__silver">{silverLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.feature}>
              <td>{row.feature}</td>
              <td>{row.free}</td>
              <td className="pricing-table__silver">{row.silver}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="pricing-card__cta">
      <button
        type="button"
        className="btn secondary"
        title={
          alreadySubscribed
            ? 'Your Silver subscription is already active.'
            : paymentLive
              ? 'Sign in required before payment'
              : 'Payment link is not live yet.'
        }
        onClick={onChoose}
        disabled={!paymentLive || !!processingPayment || !!alreadySubscribed}
      >
        {alreadySubscribed ? 'Already Subscribed' : processingPayment ? 'Starting Payment...' : ctaLabel}
      </button>
      <button type="button" className="btn ghost" onClick={onTalkToSales}>
        Talk to Sales
      </button>
    </div>
  </section>
);

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [selectedRole, setSelectedRole] = useState<'candidate' | 'recruiter'>('candidate');
  const [linksByPlan, setLinksByPlan] = useState<Record<string, any>>({});
  const [publicSettings, setPublicSettings] = useState<Record<string, number>>({});
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [affiliateOffer, setAffiliateOffer] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const roleLockedPricing = isAuthenticated && (user?.role === 'candidate' || user?.role === 'recruiter');

  useEffect(() => {
    if (!roleLockedPricing || !user) return;
    setSelectedRole(user.role as 'candidate' | 'recruiter');
  }, [roleLockedPricing, user]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingLinks(true);
        setError(null);
        const [paymentLinksResp, settingsResp] = await Promise.all([
          paymentsAPI.listLiveLinks(),
          settingsAPI.getPublicSettings()
        ]);
        const map: Record<string, any> = {};
        (paymentLinksResp.links || []).forEach((link: any) => {
          map[link.plan_key] = link;
        });
        setLinksByPlan(map);
        setPublicSettings(settingsResp.settings || {});
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Unable to load payment links.');
      } finally {
        setLoadingLinks(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadAffiliateOffer = async () => {
      if (!isAuthenticated || !user || user.role !== 'candidate') {
        setAffiliateOffer(null);
        return;
      }
      try {
        const response = await paymentsAPI.getAffiliateOffer();
        setAffiliateOffer(response.offer || null);
      } catch {
        setAffiliateOffer(null);
      }
    };
    loadAffiliateOffer();
  }, [isAuthenticated, user]);

  const effectiveRole: 'candidate' | 'recruiter' = roleLockedPricing
    ? (user?.role === 'recruiter' ? 'recruiter' : 'candidate')
    : selectedRole;

  const activePlan = useMemo(() => {
    if (effectiveRole === 'candidate') {
      const link = linksByPlan['candidate_silver'];
      const candidateRows: PlanRow[] = [
        { feature: 'Number of Applies', free: String(publicSettings['pricing.candidate.free.applies_monthly'] ?? 10), silver: `${publicSettings['pricing.candidate.silver.applies_monthly'] ?? 75} Monthly` },
        { feature: 'Number of Searches', free: String(publicSettings['pricing.candidate.free.searches_monthly'] ?? 50), silver: `${publicSettings['pricing.candidate.silver.searches_monthly'] ?? 150} Monthly` },
        { feature: 'Priority Queue', free: 'NA', silver: 'Yes' },
        { feature: 'Priority Support', free: 'NA', silver: 'Yes' },
        { feature: 'Personalized Emails', free: 'NA', silver: 'Yes' }
      ];
      return {
        planKey: 'candidate_silver',
        title: 'Candidates',
        subtitle: 'Grow visibility and applications with a monthly boost.',
        freeLabel: 'Free',
        silverLabel: `Silver - INR ${affiliateOffer?.amount_inr ?? link?.amount ?? publicSettings['pricing.candidate.silver.price_inr'] ?? 35} / month`,
        rows: candidateRows,
        ctaLabel: affiliateOffer ? `Pay Affiliate Offer (INR ${affiliateOffer.amount_inr})` : 'Choose Candidate Silver',
        paymentLive: Boolean(link?.is_live)
      };
    }

    const link = linksByPlan['recruiter_silver'];
    const recruiterRows: PlanRow[] = [
      { feature: 'Job Postings', free: `${publicSettings['pricing.recruiter.free.job_postings_monthly'] ?? 5} Monthly`, silver: `${publicSettings['pricing.recruiter.silver.job_postings_monthly'] ?? 10} Monthly` },
      { feature: 'Talent Views', free: `${publicSettings['pricing.recruiter.free.talent_views_monthly'] ?? 25} Monthly`, silver: `${publicSettings['pricing.recruiter.silver.talent_views_monthly'] ?? 120} Monthly` },
      { feature: 'Buckets', free: String(publicSettings['pricing.recruiter.free.buckets_total'] ?? 5), silver: String(publicSettings['pricing.recruiter.silver.buckets_total'] ?? 15) },
      { feature: 'Number of Searches', free: `${publicSettings['pricing.recruiter.free.searches_monthly'] ?? 50} Monthly`, silver: `${publicSettings['pricing.recruiter.silver.searches_monthly'] ?? 200} Monthly` },
      { feature: 'Priority Queue', free: 'NA', silver: 'Yes' },
      { feature: 'Priority Support', free: 'NA', silver: 'Yes' },
      { feature: 'Personalised Emails', free: 'NA', silver: 'Yes' },
      { feature: 'Org Limits', free: 'Applicable', silver: 'Customizable' },
      { feature: 'Org Management', free: 'NA', silver: 'Yes' }
    ];
    return {
      planKey: 'recruiter_silver',
      title: 'Recruiters',
      subtitle: 'Scale job posting and talent discovery with better limits.',
      freeLabel: 'Free',
      silverLabel: `Silver - INR ${link?.amount ?? publicSettings['pricing.recruiter.silver.price_inr'] ?? 2000} / user`,
      rows: recruiterRows,
      ctaLabel: 'Choose Recruiter Silver',
      paymentLive: Boolean(link?.is_live)
    };
  }, [affiliateOffer, effectiveRole, linksByPlan, publicSettings]);

  const alreadySubscribed = useMemo(() => {
    if (!isAuthenticated || !user) return false;
    if (user.subscription_tier !== 'silver') return false;
    if (effectiveRole === 'candidate' && user.role === 'candidate') return true;
    if (effectiveRole === 'recruiter' && user.role === 'recruiter') return true;
    return false;
  }, [effectiveRole, isAuthenticated, user]);

  const handleChoose = async () => {
    if (!activePlan.paymentLive) return;
    if (alreadySubscribed) return;
    if (!isAuthenticated) {
      setError('Please sign in or register before making payment.');
      navigate('/login');
      return;
    }
    try {
      setError(null);
      setProcessingPayment(true);
      const response = effectiveRole === 'candidate' && affiliateOffer
        ? await paymentsAPI.initiateAffiliatePayment()
        : await paymentsAPI.initiatePayment(activePlan.planKey);
      if (!response?.payment_url) {
        throw new Error('Payment URL is missing.');
      }
      window.open(response.payment_url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Unable to start payment.');
    } finally {
      setProcessingPayment(false);
    }
  };

  return (
    <div className="pricing-page">
      <div className="container">
        <section className="pricing-hero card">
          <span className="badge yellow">Launch Offer</span>
          <h1 className="h1">Pricing Plans Built For Faster Hiring Outcomes</h1>
          <p className="muted">
            Start free, then upgrade to Silver for higher monthly limits, faster queues, and personalized support.
          </p>
          <div className="pricing-hero__chips">
            <span className="pill">No setup fee</span>
            <span className="pill">Cancel anytime</span>
            <span className="pill">{loadingLinks ? 'Loading payment links...' : 'Pay securely via payment link'}</span>
          </div>
        </section>
        {error && <div className="alert error alert--stacked">{error}</div>}
        {affiliateOffer && effectiveRole === 'candidate' && (
          <div className="alert success alert--stacked pricing-affiliate-alert">
            Affiliate offer detected: <strong>{affiliateOffer.component_name || 'Affiliate Candidate Silver'}</strong> at <strong>{affiliateOffer.currency || 'INR'} {affiliateOffer.amount_inr}</strong>.
          </div>
        )}

        <div className="grid pricing-grid">
          {!roleLockedPricing && (
            <div className="pricing-role-switch" role="tablist" aria-label="Choose pricing role">
              <button
                type="button"
                className={`pill ${selectedRole === 'candidate' ? 'active' : ''}`}
                onClick={() => setSelectedRole('candidate')}
                aria-pressed={selectedRole === 'candidate'}
              >
                Candidate Pricing
              </button>
              <button
                type="button"
                className={`pill ${selectedRole === 'recruiter' ? 'active' : ''}`}
                onClick={() => setSelectedRole('recruiter')}
                aria-pressed={selectedRole === 'recruiter'}
              >
                Recruiter Pricing
              </button>
            </div>
          )}

          <PricingTable
            title={activePlan.title}
            subtitle={activePlan.subtitle}
            freeLabel={activePlan.freeLabel}
            silverLabel={activePlan.silverLabel}
            rows={activePlan.rows}
            ctaLabel={alreadySubscribed ? 'Already Subscribed' : activePlan.ctaLabel}
            paymentLive={activePlan.paymentLive}
            processingPayment={processingPayment}
            alreadySubscribed={alreadySubscribed}
            onChoose={handleChoose}
            onTalkToSales={() => navigate('/talk-to-sales')}
          />
        </div>
      </div>
    </div>
  );
};

export default Pricing;
